import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { FiFilter } from "react-icons/fi";
import SessionResult from "./SessionResult";
import BookingModal from "./Modals/BookingModal";
import BookingSuccessModal from "./Modals/BookingSuccessModal";
import { useDispatch, useSelector } from "react-redux";
import { searchSessionWithFilters } from "../../redux/actions/mindfulness";
import { useDebounce } from "./utils";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

// Styled Components
const Container = styled.div`
  box-sizing: border-box;
  padding: 20px;
  width: 80%;
  margin: 0 auto;
  font-family: Montserrat;
  margin-top: 7.5rem;
  font-weight: 400;
  border-radius: 15px;
  box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.25);
  background: rgba(255, 255, 255, 1);
`;

const Header = styled.div`
  margin-bottom: 20px;
  h1 {
    font-size: 1.875rem;
    font-weight: 400;
    color: #333;
    margin-bottom: 4px;
  }
  p {
    color: #6a6a6a;
    font-size: 1rem;
  }
`;

const TabsContainer = styled.div`
  border-bottom: 1px solid #ddd;
  margin-bottom: 20px;
  position: relative;
  width: 100%;
`;

const Tabs = styled.div`
  display: flex;
  gap: 30px;
  width: 100%;
`;

// const Tab = styled.div`
//   padding: 18px 0;
//   cursor: pointer;
//   position: relative;
//   color: ${(props) => (props.active ? "#FB2C36" : "#706c6c")};
//   font-weight: ${(props) => (props.active ? 500 : 400)};
//   font-size: 1rem;
//   width: 100%;
//   text-align: center;

//   &::after {
//     content: "";
//     position: absolute;
//     bottom: -1px;
//     left: 0;
//     width: 100%;
//     height: 3px;
//     background-color: ${(props) => (props.active ? "#e53935" : "transparent")};
//     transition: all 0.3s ease;
//   }

//   &:hover {
//     color: ${(props) => (props.active ? "#e53935" : "#333")};
//   }
// `;

const Tab = styled.div`
  padding: 18px 0;
  position: relative;
  color: ${(props) => {
    if (props.disabled) return "#706c6c";
    return props.active ? "#FB2C36" : "#706c6c";
  }};
  font-weight: ${(props) => (props.disabled ? 400 : props.active ? 500 : 400)};
  font-size: 1rem;
  width: 100%;
  text-align: center;

  /* Disable pointer events and cursor */
  ${(props) =>
    props.disabled &&
    `
    cursor: not-allowed;
    pointer-events: none;
    opacity: 0.5;
  `}

  &::after {
    content: "";
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 3px;
    background-color: ${(props) =>
      props.disabled || !props.active ? "transparent" : "#e53935"};
    transition: all 0.3s ease;
  }

  /* Disable hover when disabled or active */
  &:hover {
    ${(props) =>
      !props.disabled &&
      !props.active &&
      `
      color: #333;
    `}
  }
`;

const TopContainer = styled.div`
  padding: 1.5rem;
  box-sizing: border-box;
  border: 1px solid rgba(229, 231, 235, 1);
  border-radius: 14px;
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.1),
    0px 1px 2px -1px rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 1);
`;

const SearchBar = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  padding: 10px;
  border-radius: 8px;
  background: #fff;
  width: 100%;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 12px 15px;
  border: 1px solid #d1d5dc;
  border-radius: 10px;
  font-size: 14px;
  outline: none;
  transition: border 0.3s;
  width: 100%;
  background-color: #f9fafb;

  &:focus {
    border-color: #706c6c;
  }
`;

const AdvancedFiltersButton = styled.button`
  background: #fb2c36;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 12px 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 1rem;
  font-weight: 500;

  &:hover {
    background: #e7000b;
  }
`;

const SessionCount = styled.div`
  color: #4a5565;
  font-size: 14px;
  font-weight: 400;
  margin: 20px 0;
`;

function SearchSessionExpert() {
  const [activeTab, setActiveTab] = useState("session");
  const [searchQuery, setSearchQuery] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const history = useHistory();

  const dispatch = useDispatch();
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const searchResult = useSelector(
    (state) => state?.mindfulness?.searchSessionResult,
  );

  useEffect(() => {
    if (debouncedSearchQuery) {
      dispatch(
        searchSessionWithFilters({
          sessionName: debouncedSearchQuery,
        }),
      );
    }
  }, [debouncedSearchQuery]);

  // Debug logging
  useEffect(() => {
    console.log("Search Result from Redux:", searchResult);
    console.log("Search Result type:", typeof searchResult);
    console.log("Is Array:", Array.isArray(searchResult));
  }, [searchResult]);

  // Normalize the sessions data - handle different data structures
  const sessions = React.useMemo(() => {
    if (!searchResult) return [];

    // If searchResult is already an array, use it
    if (Array.isArray(searchResult)) {
      return searchResult;
    }

    // If searchResult is an object with a data property
    if (searchResult.data && Array.isArray(searchResult.data)) {
      return searchResult.data;
    }

    // If searchResult is an object with a sessions property
    if (searchResult.sessions && Array.isArray(searchResult.sessions)) {
      return searchResult.sessions;
    }

    // If searchResult is an object with a results property
    if (searchResult.results && Array.isArray(searchResult.results)) {
      return searchResult.results;
    }

    return [];
  }, [searchResult]);

  return (
    <Container>
      {openModal && (
        <BookingModal
          session={selectedSession}
          onClose={(result) => {
            setOpenModal(false);
            setSelectedSession(null);
            if (result?.success) {
              setSuccessData(result);
              setOpenSuccessModal(true);
            }
          }}
        />
      )}
      {openSuccessModal && (
        <BookingSuccessModal
          bookingData={successData}
          onClose={() => {
            setOpenSuccessModal(false);
            setSuccessData(null);
          }}
        />
      )}
      <Header>
        <h1>Wellness Sessions & Expert Counselors</h1>
        <p>
          Find the perfect session or connect with verified wellness
          professionals
        </p>
      </Header>

      <TopContainer>
        <TabsContainer>
          <Tabs>
            <Tab
              active={activeTab === "session"}
              onClick={() => setActiveTab("session")}
            >
              Search by Session Name
            </Tab>
            <Tab
              active={activeTab === "expert"}
              onClick={() => setActiveTab("expert")}
              disabled
            >
              Search by Expert Name
            </Tab>
          </Tabs>
        </TabsContainer>

        <SearchBar>
          <SearchInput
            type="text"
            placeholder={
              activeTab === "session"
                ? "Search by session name..."
                : "Search by expert name..."
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <AdvancedFiltersButton>
            <FiFilter size={16} />
            Advanced Filters
          </AdvancedFiltersButton>
        </SearchBar>
      </TopContainer>

      <SessionCount>
        Showing{" "}
        <span style={{ color: "#e7000b", fontWeight: 500 }}>
          {sessions?.length || 0}{" "}
        </span>{" "}
        sessions
      </SessionCount>

      {/* Here would be the content for sessions or experts based on active tab */}
      {/* This is just a placeholder for the actual content */}
      <div
        style={{
          minHeight: "20rem",
        }}
      >
        {activeTab === "session" ? (
          // <div>
          //   <h3 style={{ color: "#333", marginBottom: "10px" }}>
          //     Available Sessions
          //   </h3>
          //   <p style={{ color: "#706c6c" }}>
          //     This area would display session results based on your search
          //   </p>
          // </div>
          <SessionResult
            sessions={sessions}
            onBooking={(data) => {
              history.push({
                pathname: "session_details",
                state: {
                  session: data,
                },
              });
            }}
          />
        ) : (
          <div>
            <h3 style={{ color: "#333", marginBottom: "10px" }}>
              Available Experts
            </h3>
            <p style={{ color: "#706c6c" }}>
              This area would display expert results based on your search
            </p>
          </div>
        )}
      </div>
    </Container>
  );
}

export default SearchSessionExpert;
